import { useEffect, useState } from "react";
import LobbyLayout from "@/components/LobbyLayout";
import { SCREEN } from "@/stores/gameStore";
import { useT } from "@/i18n";
import { fetchNews, api } from "@/lib/api";
import { toast } from "sonner";
import { Newspaper, Zap, Radio, Map as MapIcon, Wrench, Award, MessageCircle, Plus, Edit3, Shield, LogOut, Lock } from "lucide-react";

// Icon key → Lucide component. Backend stores an icon key; client resolves it.
// Add new icons here without touching the CMS.
const ICON_MAP = {
    radio: Radio,
    zap: Zap,
    map: MapIcon,
    wrench: Wrench,
    award: Award,
    message: MessageCircle,
    news: Newspaper,
};

const ADMIN_TOKEN_KEY = "axis.admin.token";

// Fallback used only if the API is unavailable (offline dev / cold DB).
const FALLBACK = [
    { id: "fb-1", tag: "OFFLINE", title: "News offline", body: "Unable to reach the news service. Showing local fallback until connection is restored.", icon: "radio", accent: "#ff9a4a", date: "" },
];

export default function NewsScreen() {
    const t = useT();
    const [items, setItems] = useState(null);
    const [error, setError] = useState(false);
    const [adminOpen, setAdminOpen] = useState(false);
    const [loginOpen, setLoginOpen] = useState(false);
    const [token, setToken] = useState(() => {
        if (typeof window === "undefined") return "";
        return sessionStorage.getItem(ADMIN_TOKEN_KEY) || "";
    });
    const signedIn = !!token;

    const reload = () => {
        fetchNews()
            .then((d) => { setItems(d); setError(false); })
            .catch(() => { setItems(FALLBACK); setError(true); });
    };

    useEffect(() => { reload(); }, []);

    const signOut = () => {
        sessionStorage.removeItem(ADMIN_TOKEN_KEY);
        setToken("");
        setAdminOpen(false);
        toast.success("Signed out of admin");
    };

    return (
        <LobbyLayout active={SCREEN.NEWS}>
            <div data-testid="news-screen" className="w-full h-full overflow-y-auto px-8 py-8">
                <div className="max-w-5xl mx-auto">
                    <div className="mb-6 flex items-center justify-between">
                        <div>
                            <div className="axis-mono text-[10px] tracking-[0.5em] text-orange-300/80">
                                {t("news.subtitle")}
                            </div>
                            <div className="axis-heading text-3xl tracking-[0.4em] text-white flex items-center gap-3">
                                <Newspaper size={22} className="text-orange-400" />
                                {t("news.title")}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {error && (
                                <div className="axis-mono text-[10px] tracking-[0.3em] text-orange-300/70">
                                    CMS OFFLINE
                                </div>
                            )}
                            {signedIn ? (
                                <>
                                    <button
                                        data-testid="news-admin-toggle"
                                        onClick={() => setAdminOpen((v) => !v)}
                                        className="axis-btn axis-cut-sm px-3 py-1.5 text-xs flex items-center gap-2 border border-orange-400 bg-orange-500/15 text-orange-200 hover:bg-orange-500/30"
                                    >
                                        <Edit3 size={12} /> {adminOpen ? "Close Admin" : "Open Admin"}
                                    </button>
                                    <button
                                        data-testid="news-admin-signout"
                                        onClick={signOut}
                                        className="axis-btn axis-btn-ghost axis-cut-sm px-3 py-1.5 text-xs flex items-center gap-2"
                                        title="Sign out of admin"
                                    >
                                        <LogOut size={12} />
                                    </button>
                                </>
                            ) : (
                                <button
                                    data-testid="news-admin-signin"
                                    onClick={() => setLoginOpen(true)}
                                    className="axis-btn axis-btn-ghost axis-cut-sm px-3 py-1.5 text-xs flex items-center gap-2"
                                    title="Editor sign in"
                                >
                                    <Shield size={12} /> Editor
                                </button>
                            )}
                        </div>
                    </div>

                    {signedIn && adminOpen && (
                        <NewsAdmin token={token} onChange={reload} onExpired={signOut} />
                    )}

                    {items === null ? (
                        <div className="axis-mono text-[10px] tracking-[0.4em] text-white/40 py-16 text-center">
                            LOADING NEWS...
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {items.map((n) => {
                                const Icon = ICON_MAP[n.icon] || Newspaper;
                                return (
                                    <article
                                        key={n.id}
                                        data-testid={`news-item-${n.id}`}
                                        className="axis-glass axis-cut-sm p-5 border border-white/10 hover:border-white/25 transition"
                                    >
                                        <div className="flex items-start gap-4">
                                            <div
                                                className="w-10 h-10 axis-cut-sm border flex items-center justify-center flex-shrink-0"
                                                style={{
                                                    borderColor: n.accent,
                                                    background: `${n.accent}18`,
                                                }}
                                            >
                                                <Icon size={17} style={{ color: n.accent }} />
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center gap-3 mb-1">
                                                    <span
                                                        className="axis-mono text-[9px] tracking-[0.4em] px-2 py-0.5 border axis-cut-sm"
                                                        style={{ borderColor: n.accent, color: n.accent }}
                                                    >
                                                        {n.tag}
                                                    </span>
                                                    <span className="axis-mono text-[10px] tracking-[0.3em] text-white/40">
                                                        {n.date}
                                                    </span>
                                                </div>
                                                <h3 className="axis-heading text-xl tracking-wider text-white">
                                                    {n.title}
                                                </h3>
                                                <p className="axis-mono text-sm text-white/70 mt-2 leading-relaxed">
                                                    {n.body}
                                                </p>
                                            </div>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {loginOpen && (
                <AdminLoginDialog
                    onClose={() => setLoginOpen(false)}
                    onSuccess={(tok) => {
                        sessionStorage.setItem(ADMIN_TOKEN_KEY, tok);
                        setToken(tok);
                        setLoginOpen(false);
                        setAdminOpen(true);
                        toast.success("Editor mode unlocked");
                    }}
                />
            )}
        </LobbyLayout>
    );
}

function AdminLoginDialog({ onClose, onSuccess }) {
    const [value, setValue] = useState("");
    const [pending, setPending] = useState(false);

    const submit = async () => {
        if (!value.trim()) {
            toast.error("Enter your editor token");
            return;
        }
        setPending(true);
        try {
            await api.post("/admin/verify", null, {
                headers: { "X-Admin-Token": value.trim() },
            });
            onSuccess(value.trim());
        } catch (e) {
            const status = e?.response?.status;
            toast.error(status === 401 ? "Invalid editor token" : "Verification failed");
        } finally {
            setPending(false);
        }
    };

    return (
        <div
            data-testid="admin-login-dialog"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md axis-fade-in"
            onClick={onClose}
        >
            <div
                className="axis-glass axis-cut max-w-md w-[92%] p-7 border-orange-400/40"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 border border-orange-400/60 axis-cut-sm">
                        <Lock size={20} className="text-orange-300" />
                    </div>
                    <div>
                        <div className="axis-mono text-[10px] tracking-[0.4em] text-orange-300/80">
                            SIGNED ADMIN
                        </div>
                        <div className="axis-heading text-xl text-white tracking-widest">
                            EDITOR SIGN-IN
                        </div>
                    </div>
                </div>
                <div className="axis-mono text-xs text-white/60 leading-relaxed mb-4">
                    Enter the shared editor token to publish or remove News cards.
                    Tokens are configured on the server via <span className="text-orange-300">NEWS_ADMIN_TOKEN</span>.
                </div>
                <input
                    data-testid="admin-token-input"
                    type="password"
                    autoFocus
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && submit()}
                    placeholder="editor token"
                    className="w-full bg-black/60 border border-white/20 axis-cut-sm px-4 py-3 text-white axis-mono tracking-widest focus:border-orange-400 outline-none"
                />
                <div className="mt-5 flex justify-end gap-3">
                    <button
                        data-testid="admin-login-cancel"
                        onClick={onClose}
                        className="axis-btn axis-btn-ghost axis-cut-sm px-5 py-2 text-xs"
                    >
                        Cancel
                    </button>
                    <button
                        data-testid="admin-login-submit"
                        onClick={submit}
                        disabled={pending}
                        className="axis-btn axis-cut-sm px-5 py-2 text-xs border border-orange-400 bg-orange-500/20 text-orange-200 hover:bg-orange-500/40 hover:text-white transition flex items-center gap-2 disabled:opacity-50"
                    >
                        <Shield size={13} /> {pending ? "VERIFYING..." : "SIGN IN"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function NewsAdmin({ token, onChange, onExpired }) {
    const [form, setForm] = useState({
        tag: "ANNOUNCEMENT", title: "", body: "", icon: "radio", accent: "#ff9a4a", date: "", order: 100,
    });
    const set = (k, v) => setForm({ ...form, [k]: v });

    const submit = async () => {
        if (!form.title || !form.body) {
            toast.error("Title and body required");
            return;
        }
        try {
            await api.post("/news", form, {
                headers: { "X-Admin-Token": token },
            });
            toast.success("Card published");
            setForm({ ...form, title: "", body: "" });
            onChange();
        } catch (e) {
            const status = e?.response?.status;
            if (status === 401) {
                toast.error("Session expired — sign in again");
                onExpired?.();
            } else {
                toast.error("Publish failed");
            }
        }
    };

    return (
        <div
            data-testid="news-admin-panel"
            className="axis-glass axis-cut-sm p-5 border border-orange-400/40 mb-4"
        >
            <div className="axis-mono text-[10px] tracking-[0.4em] text-orange-300/80 mb-3">
                ADMIN · NEW CARD · SIGNED SESSION
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                <input data-testid="admin-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Title" className="col-span-2 bg-black/60 border border-white/20 axis-cut-sm px-3 py-2 text-white text-sm axis-heading tracking-wider focus:border-orange-400 outline-none" />
                <input value={form.tag} onChange={(e) => set("tag", e.target.value.toUpperCase())} placeholder="TAG" className="bg-black/60 border border-white/20 axis-cut-sm px-3 py-2 text-white text-xs axis-mono tracking-widest focus:border-orange-400 outline-none" />
                <input value={form.date} onChange={(e) => set("date", e.target.value)} placeholder="DATE / VERSION" className="bg-black/60 border border-white/20 axis-cut-sm px-3 py-2 text-white text-xs axis-mono tracking-widest focus:border-orange-400 outline-none" />
            </div>
            <textarea data-testid="admin-body" value={form.body} onChange={(e) => set("body", e.target.value)} placeholder="Body copy..." rows={3} className="w-full bg-black/60 border border-white/20 axis-cut-sm px-3 py-2 text-white text-sm axis-mono focus:border-orange-400 outline-none mb-3" />
            <div className="grid grid-cols-3 gap-2 mb-3">
                <select value={form.icon} onChange={(e) => set("icon", e.target.value)} className="bg-black/60 border border-white/20 axis-cut-sm px-3 py-2 text-white text-xs axis-mono tracking-widest focus:border-orange-400 outline-none">
                    {["radio","zap","map","wrench","award","message","news"].map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
                <input type="color" value={form.accent} onChange={(e) => set("accent", e.target.value)} className="bg-black/60 border border-white/20 axis-cut-sm px-3 py-1 h-9" />
                <input type="number" value={form.order} onChange={(e) => set("order", parseInt(e.target.value) || 0)} placeholder="Order" className="bg-black/60 border border-white/20 axis-cut-sm px-3 py-2 text-white text-xs axis-mono focus:border-orange-400 outline-none" />
            </div>
            <div className="flex justify-end">
                <button
                    data-testid="admin-publish"
                    onClick={submit}
                    className="axis-btn axis-cut-sm px-6 py-2 border border-orange-400 bg-orange-500/20 text-orange-200 hover:bg-orange-500/40 hover:text-white transition flex items-center gap-2 text-xs"
                >
                    <Plus size={13} /> PUBLISH
                </button>
            </div>
        </div>
    );
}
